/**
 * InputDinheiro — campo de dinheiro com máscara R$ (ADR-0059).
 *
 * Digitação livre pt-BR (`parseDinheiro`): "1500,5", "1.500,50", "80000".
 * Enquanto a pessoa digita o texto é dela; no blur o valor é normalizado
 * para "1.500,50" (`formatarDinheiro`). O pai recebe SEMPRE um Number ou
 * null (vazio) por `onChange` — nunca o texto — e é ele quem manda o
 * número ao backend.
 *
 * Não escreva outro parser: `utils/dinheiro.js` é a convenção única.
 */
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { formatarDinheiro, parseDinheiro } from "../../utils/dinheiro";

const Wrap = styled.span`
  display: flex;
  align-items: center;
  gap: 0;
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  overflow: hidden;

  &:focus-within {
    border-color: var(--accent);
  }

  .prefixo {
    padding: 0 8px 0 10px;
    font-size: 12.5px;
    color: var(--ink-3);
    white-space: nowrap;
    user-select: none;
  }

  input {
    flex: 1;
    min-width: 0;
    height: 34px;
    border: 0;
    background: transparent;
    color: var(--ink);
    font: inherit;
    font-variant-numeric: tabular-nums;
    text-align: right;
    padding: 0 10px 0 0;

    &:focus {
      outline: none;
    }
    &:disabled {
      color: var(--ink-4);
    }
  }
`;

function textoDe(valor) {
  if (valor === null || valor === undefined || valor === "") return "";
  const n = Number(valor);
  return Number.isFinite(n) ? formatarDinheiro(n) : "";
}

export default function InputDinheiro({
  value,
  onChange,
  placeholder = "0,00",
  disabled = false,
  id,
  name,
  "aria-label": ariaLabel,
  autoFocus = false,
}) {
  const [texto, setTexto] = useState(() => textoDe(value));

  // O valor de fora mudou (carregou o registro, reset do form): reflete.
  useEffect(() => {
    setTexto((atual) => {
      const externo = textoDe(value);
      // Não pisa no que a pessoa está digitando se der o mesmo número.
      return parseDinheiro(atual) === parseDinheiro(externo) ? atual : externo;
    });
  }, [value]);

  const aoDigitar = (e) => {
    const t = e.target.value;
    setTexto(t);
    onChange(parseDinheiro(t));
  };

  const aoSair = () => {
    const n = parseDinheiro(texto);
    setTexto(n === null ? "" : formatarDinheiro(n));
  };

  return (
    <Wrap>
      <span className="prefixo">R$</span>
      <input
        id={id}
        name={name}
        type="text"
        inputMode="decimal"
        aria-label={ariaLabel}
        autoFocus={autoFocus}
        value={texto}
        placeholder={placeholder}
        disabled={disabled}
        onChange={aoDigitar}
        onBlur={aoSair}
      />
    </Wrap>
  );
}
